// src/pages/storePages/manageStore/ManageStoreOrders.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  storeGetAllOrders,
  storeConfirmOrder,
  storeCancelOrder,
  storeGetOrderStats,
} from "../../../storeAPIs/StoreOrdersApis";
import OrderDetailDialog from "./OrderDetailDialog";
import "../../../static/storecss/manageStore/ManageStoreOrders.css";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "confirmed", label: "Confirmed" },
  { id: "cancelled", label: "Cancelled" },
];

const METHOD_LABELS = {
  mpesa: "M-Pesa",
  bank: "Bank",
  paypal: "PayPal",
};

const PAGE_SIZE = 20;

export default function ManageStoreOrders() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL is the source of truth ──
  const page = parseInt(searchParams.get("page") || "1", 10);
  const statusFilter = searchParams.get("status") || "all";
  const search = searchParams.get("search") || "";

  // Local input mirror + debounce
  const [searchInput, setSearchInput] = useState(search);
  const debounceRef = useRef(null);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // ── Orders state (paginated, server-filtered) ──
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // ── Stats state (loaded once, refreshed on demand) ──
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    revenue: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Modal
  const [detailId, setDetailId] = useState(null);

  // ── Build API params from URL ──
  const buildParams = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };

    if (statusFilter === "pending") params.status = "pending";
    else if (statusFilter === "confirmed") params.is_confirmed = true;
    else if (statusFilter === "cancelled") params.status = "cancelled";

    if (search) params.search = search;

    return params;
  }, [page, statusFilter, search]);

  // ── Fetch orders ──
  const loadOrders = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    storeGetAllOrders(buildParams())
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? {};
        setOrders(data.results ?? (Array.isArray(data) ? data : []));
        setCount(
          data.count ??
            (data.results?.length || (Array.isArray(data) ? data.length : 0))
        );
        setHasNext(Boolean(data.next));
        setHasPrev(Boolean(data.previous));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load orders.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [buildParams]);

  // ── Fetch stats (always global, unaffected by filters) ──
  const loadStats = useCallback(() => {
    let cancelled = false;
    setStatsLoading(true);

    storeGetOrderStats()
      .then((res) => {
        if (!cancelled) {
          setStats({
            total: res.data?.total ?? 0,
            pending: res.data?.pending ?? 0,
            confirmed: res.data?.confirmed ?? 0,
            revenue: res.data?.revenue ?? 0,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats({ total: 0, pending: 0, confirmed: 0, revenue: 0 });
        }
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh both
  const load = useCallback(() => {
    loadOrders();
    loadStats();
  }, [loadOrders, loadStats]);

  useEffect(() => {
    const cleanup = loadOrders();
    return cleanup;
  }, [loadOrders]);

  useEffect(() => {
    const cleanup = loadStats();
    return cleanup;
  }, [loadStats]);

  // ── URL helpers ──
  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParam("search", value);
    }, 350);
  };

  const goToPage = (n) => updateParam("page", String(n));

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
    setSearchInput("");
  };

  // ── Row actions ──
  const patchOrder = (updated) =>
    setOrders((prev) =>
      prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
    );

  const handleConfirm = async (order) => {
    if (busyId) return;
    setBusyId(order.id);
    try {
      const res = await storeConfirmOrder(order.id);
      patchOrder(res.data);
      loadStats(); // stats change on confirm
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Failed to confirm.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (order) => {
    if (busyId) return;
    setBusyId(order.id);
    try {
      const res = await storeCancelOrder(order.id);
      patchOrder(res.data);
      loadStats(); // stats change on cancel
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Failed to cancel.");
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const hasAnyFilter = statusFilter !== "all" || Boolean(search);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / PAGE_SIZE)),
    [count]
  );

  return (
    <div className="mso-page">
      <header className="mso-header">
        <div>
          <h1>Orders</h1>
          <p>Verify payments and process customer orders.</p>
        </div>
        <button
          type="button"
          className="mso-refresh"
          onClick={load}
          disabled={loading || statsLoading}
        >
          <i className="fa-solid fa-rotate" />
          {loading || statsLoading ? "Loading…" : "Refresh"}
        </button>
      </header>

      {/* ══ Stats cards (global, always visible) ══ */}
      <section className="mso-stats">
        <div className="mso-stat">
          <span className="mso-stat-label">Total</span>
          <span className="mso-stat-value">{stats.total}</span>
        </div>
        <div className="mso-stat mso-stat--warn">
          <span className="mso-stat-label">Pending</span>
          <span className="mso-stat-value">{stats.pending}</span>
        </div>
        <div className="mso-stat mso-stat--ok">
          <span className="mso-stat-label">Confirmed</span>
          <span className="mso-stat-value">{stats.confirmed}</span>
        </div>
        <div className="mso-stat">
          <span className="mso-stat-label">Revenue</span>
          <span className="mso-stat-value mso-stat-value--money">
            KES {stats.revenue.toLocaleString()}
          </span>
        </div>
      </section>

      {/* ══ Toolbar ══ */}
      <div className="mso-toolbar">
        <div className="mso-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`mso-filter ${
                statusFilter === f.id ? "is-active" : ""
              }`}
              onClick={() => updateParam("status", f.id === "all" ? "" : f.id)}
            >
              {f.label}
              {f.id === "pending" && stats.pending > 0 && (
                <span className="mso-filter-count">{stats.pending}</span>
              )}
            </button>
          ))}
        </div>

        <div className="mso-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="text"
            placeholder="Search order, customer, reference…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className="mso-search-clear"
              onClick={() => handleSearchChange("")}
              aria-label="Clear search"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>

        {hasAnyFilter && (
          <button
            type="button"
            className="mso-clear-filters"
            onClick={clearAllFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {error && <div className="mso-alert">{error}</div>}

      {loading && orders.length === 0 ? (
        <div className="mso-loading">
          <span className="mso-spinner" />
          Loading orders…
        </div>
      ) : orders.length === 0 ? (
        <div className="mso-empty">
          <i className="fa-solid fa-receipt" />
          <h2>{hasAnyFilter ? "No matches" : "No orders yet"}</h2>
          <p>
            {hasAnyFilter
              ? "Try a different filter or search."
              : "Orders will show up here as customers check out."}
          </p>
          {hasAnyFilter && (
            <button
              type="button"
              className="mso-btn mso-btn--ghost"
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className={`mso-table-wrap ${loading ? "is-loading" : ""}`}>
            <table className="mso-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th className="mso-th-right">Total</th>
                  <th>Status</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <span className="mso-order-id">#{o.id}</span>
                    </td>
                    <td>
                      <div className="mso-cust">
                        <strong>{o.customer_name || o.username}</strong>
                        {o.customer_phone && (
                          <span className="mso-cust-sub">
                            {o.customer_phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="mso-muted">{formatDate(o.created_at)}</td>
                    <td>
                      <span
                        className={`mso-method mso-method--${o.payment_method}`}
                      >
                        {METHOD_LABELS[o.payment_method] ||
                          o.payment_method_display}
                      </span>
                    </td>
                    <td className="mso-ref">
                      {o.transaction_id ? (
                        <code>{o.transaction_id}</code>
                      ) : (
                        <span className="mso-muted">—</span>
                      )}
                    </td>
                    <td className="mso-total">
                      KES {Number(o.total).toLocaleString()}
                    </td>
                    <td>
                      <span
                        className={`mso-status ${
                          o.status === "cancelled"
                            ? "is-cancelled"
                            : o.is_confirmed
                            ? "is-confirmed"
                            : "is-pending"
                        }`}
                      >
                        {o.status === "cancelled"
                          ? "Cancelled"
                          : o.is_confirmed
                          ? "Confirmed"
                          : "Pending"}
                      </span>
                    </td>
                    <td className="mso-actions">
                      {!o.is_confirmed && o.status !== "cancelled" && (
                        <>
                          <button
                            type="button"
                            className="mso-btn mso-btn--primary"
                            onClick={() => handleConfirm(o)}
                            disabled={busyId === o.id}
                          >
                            <i className="fa-solid fa-check" />
                            Confirm
                          </button>
                          <button
                            type="button"
                            className="mso-btn mso-btn--ghost"
                            onClick={() => handleCancel(o)}
                            disabled={busyId === o.id}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="mso-btn mso-btn--ghost"
                        onClick={() => setDetailId(o.id)}
                        title="View details"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(hasNext || hasPrev || totalPages > 1) && (
            <div className="mso-pagination">
              <button
                type="button"
                className="mso-page-btn"
                onClick={() => goToPage(page - 1)}
                disabled={!hasPrev || loading}
              >
                <i className="fa-solid fa-chevron-left" /> Previous
              </button>

              <span className="mso-page-status">
                Page <strong>{page}</strong>
                {totalPages > 1 && ` of ${totalPages}`}
              </span>

              <button
                type="button"
                className="mso-page-btn"
                onClick={() => goToPage(page + 1)}
                disabled={!hasNext || loading}
              >
                Next <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}

      <OrderDetailDialog
        open={!!detailId}
        orderId={detailId}
        onClose={() => setDetailId(null)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}